import React from 'react';
import {
  AlignCenter,
  AlignLeft,
  AlignRight,
  ImagePlus,
  Lock,
  MapPinned,
  Minus,
  Plus,
  Trash2,
  Unlock,
} from 'lucide-react';
import { LoadingSpinner } from '../../../../components/LoadingSpinner';
import {
  LAYOUT_FIELD_DEFS,
  LAYOUT_LINE_MIN_THICKNESS_PT,
  PDF_A4_WIDTH_PT,
  isLabeledFieldKey,
  lineThicknessPt,
  minElementHeightPt,
  minElementWidthPt,
  resolveLineOrientation,
  type LayoutBand,
  type LayoutElement,
  type LayoutElementKey,
} from '../../utils/documentLayoutTypes';
import {
  LAYOUT_MERGE_TOKENS,
  defaultLabelForKey,
  resolveLabelPlacement,
  tokenChipLabel,
} from '../../utils/layoutFieldPresentation';
import { formatPtValue, parsePtValue } from './layoutEditorUtils';
import {
  SETTINGS_BTN_DANGER_OUTLINE,
  SETTINGS_BTN_OUTLINE,
  SETTINGS_FIELD_LABEL,
  SETTINGS_INPUT_CLASS,
  SETTINGS_TEXTAREA_CLASS,
} from '../../utils/settingsSectionStyles';

type Props = {
  element: LayoutElement | null;
  band: 'header' | 'footer' | null;
  /** Height of the band the element lives in — upper bound for the Höhe field. */
  bandHeightPt: number;
  assetPreviewUrl?: string | null;
  assetUploading?: boolean;
  onUploadAsset?: (file: File) => void | Promise<void>;
  onClearAsset?: () => void;
  onSnapRecipientToDinWindow?: () => void;
  onChange: (patch: Partial<LayoutElement>) => void;
  onLock: () => void;
  onRemove: () => void;
  canRemove: boolean;
};

const TEXT_SWATCHES = ['#111827', '#374151', '#64748b', '#1d4ed8', '#2563eb', '#dc2626', '#f59e0b'];
const BG_SWATCHES = ['#dbeafe', '#eff6ff', '#fef3c7', '#fee2e2', '#ede9fe', '#f3f4f6', '#ffffff'];

function elementLabel(key: LayoutElementKey, band: LayoutBand): string {
  const def = LAYOUT_FIELD_DEFS.find((d) => d.key === key && d.band === band);
  return def?.label ?? key;
}

function Section({
  title,
  children,
  className = '',
}: {
  title?: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <section className={`space-y-2.5 ${className}`}>
      {title ? <p className={SETTINGS_FIELD_LABEL}>{title}</p> : null}
      {children}
    </section>
  );
}

function Divider() {
  return <div className="my-4 border-t border-app-border" />;
}

function snapStepper(n: number, step: number, min: number, max: number): number {
  const snapped = Math.round(n / step) * step;
  const places = step < 1 ? 2 : 0;
  return Math.min(max, Math.max(min, Number(snapped.toFixed(places))));
}

/**
 * Point field that keeps the typed text while focused — clamping per keystroke
 * would snap „0,5“ back to the minimum before the decimals are typed.
 */
function PtInput({
  label,
  value,
  min,
  max,
  step,
  disabled,
  onCommit,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  disabled?: boolean;
  onCommit: (n: number) => void;
}) {
  const [draft, setDraft] = React.useState<string | null>(null);

  const commit = () => {
    if (draft === null) return;
    const parsed = parsePtValue(draft);
    setDraft(null);
    if (parsed === null) return;
    onCommit(snapStepper(parsed, step, min, max));
  };

  return (
    <div>
      <p className="mb-1 text-[11px] font-medium text-app-text-subtle">{label}</p>
      <div className="relative">
        <input
          type="text"
          inputMode="decimal"
          className={`${SETTINGS_INPUT_CLASS} !h-8 pr-7 tabular-nums`}
          value={draft ?? formatPtValue(value)}
          disabled={disabled}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') e.currentTarget.blur();
            if (e.key === 'Escape') setDraft(null);
          }}
          aria-label={label}
        />
        <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-slate-400">
          pt
        </span>
      </div>
    </div>
  );
}

function Stepper({
  label,
  value,
  min,
  max,
  step = 1,
  suffix = 'pt',
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step?: number;
  suffix?: string;
  onChange: (n: number) => void;
}) {
  const [draft, setDraft] = React.useState<string | null>(null);

  const commit = () => {
    if (draft === null) return;
    const parsed = parsePtValue(draft);
    setDraft(null);
    if (parsed === null) return;
    onChange(snapStepper(parsed, step, min, max));
  };

  return (
    <div>
      <p className={`${SETTINGS_FIELD_LABEL} mb-1.5`}>{label}</p>
      <div className="flex items-center gap-1">
        <button
          type="button"
          className={`${SETTINGS_BTN_OUTLINE} !h-8 !w-8 !px-0`}
          disabled={value <= min}
          onClick={() => onChange(snapStepper(value - step, step, min, max))}
          aria-label={`${label} verringern`}
        >
          <Minus className="h-3.5 w-3.5" />
        </button>
        <div className="relative min-w-0 flex-1">
          <input
            type="text"
            inputMode="decimal"
            className={`${SETTINGS_INPUT_CLASS} !h-8 pr-8 text-center tabular-nums`}
            value={draft ?? (Number.isInteger(step) ? String(Math.round(value)) : formatPtValue(value))}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => {
              if (e.key === 'Enter') e.currentTarget.blur();
              if (e.key === 'Escape') setDraft(null);
            }}
            aria-label={label}
          />
          <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[11px] text-slate-400">
            {suffix}
          </span>
        </div>
        <button
          type="button"
          className={`${SETTINGS_BTN_OUTLINE} !h-8 !w-8 !px-0`}
          disabled={value >= max}
          onClick={() => onChange(snapStepper(value + step, step, min, max))}
          aria-label={`${label} erhöhen`}
        >
          <Plus className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}

function ToggleGroup({
  children,
}: {
  children: React.ReactNode;
}) {
  return (
    <div className="flex h-8 gap-0.5 rounded-md border border-app-border bg-app-surface-muted p-0.5">{children}</div>
  );
}

function ToggleBtn({
  active,
  onClick,
  title,
  children,
  className = '',
}: {
  active: boolean;
  onClick: () => void;
  title: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <button
      type="button"
      title={title}
      aria-pressed={active}
      onClick={onClick}
      className={`inline-flex h-full flex-1 items-center justify-center rounded-[4px] text-xs transition-colors ${
        active
          ? 'bg-app-surface text-app-text shadow-sm ring-1 ring-slate-200'
          : 'text-app-text-subtle hover:text-app-text'
      } ${className}`}
    >
      {children}
    </button>
  );
}

export function LayoutInspector({
  element,
  band,
  bandHeightPt,
  assetPreviewUrl = null,
  assetUploading = false,
  onUploadAsset,
  onClearAsset,
  onSnapRecipientToDinWindow,
  onChange,
  onLock,
  onRemove,
  canRemove,
}: Props) {
  const shell =
    'flex w-[298px] shrink-0 flex-col overflow-hidden border-l border-app-border bg-app-surface self-stretch';

  if (!element || !band) {
    return (
      <aside className={shell}>
        <div className="border-b border-app-border px-4 py-4">
          <h2 className="text-sm font-semibold text-app-text">Eigenschaften</h2>
          <p className="mt-0.5 text-xs text-app-text-subtle">Ausgewähltes Element</p>
        </div>
        <div className="flex flex-1 items-center justify-center px-4 text-center">
            <div>
              <p className="text-sm font-medium text-app-text-muted">Kein Element gewählt</p>
              <p className="mt-1 text-xs leading-relaxed text-slate-400">
                Klicke ein Feld auf der Vorlage, um Stil und Position anzupassen.
              </p>
            </div>
          </div>
      </aside>
    );
  }

  const mandatory = !canRemove;
  const isTextEl = !['divider', 'colorBlock', 'logo', 'headerBanner', 'signatureImage'].includes(
    element.key,
  );
  const isImgEl = ['logo', 'headerBanner', 'signatureImage', 'colorBlock'].includes(element.key);
  const isUploadableAsset = element.key === 'headerBanner' || element.key === 'signatureImage';
  const isLabeled = isLabeledFieldKey(element.key);
  // Only a line's thickness axis keeps quarter points; every other box is whole-point.
  const verticalLine = element.key === 'divider' && resolveLineOrientation(element) === 'vertical';
  const widthIsThickness = element.key === 'divider' && verticalLine;
  const heightIsThickness = element.key === 'divider' && !verticalLine;
  const widthMin = widthIsThickness ? minElementWidthPt(element.key) : 2;
  const heightMin = heightIsThickness ? minElementHeightPt(element.key) : 1;
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);
  const freeTextRef = React.useRef<HTMLTextAreaElement | null>(null);
  const colorProp = element.key === 'colorBlock' ? 'bg' : 'color';
  const colorVal =
    (colorProp === 'bg' ? element.bg : element.color) ||
    (element.key === 'colorBlock' ? '#eff6ff' : '#374151');
  const swatches = element.key === 'colorBlock' ? BG_SWATCHES : TEXT_SWATCHES;
  const label = elementLabel(element.key, band);
  const fontSize = element.fontSizePt ?? 9.5;
  const labelPlacement = resolveLabelPlacement(element);
  const labelFontSize = element.labelFontSizePt ?? (labelPlacement === 'stacked' ? Math.max(5, fontSize - 0.5) : fontSize);
  const valueBoldActive = isLabeled ? element.bold !== false : !!element.bold;
  const defaultFieldLabel = defaultLabelForKey(element.key);

  const insertFreeTextToken = (token: string) => {
    const snippet = `{{${token}}}`;
    const el = freeTextRef.current;
    const current = element.text ?? '';
    if (!el) {
      onChange({ text: `${current}${snippet}` });
      return;
    }
    const start = el.selectionStart ?? current.length;
    const end = el.selectionEnd ?? current.length;
    const next = current.slice(0, start) + snippet + current.slice(end);
    onChange({ text: next });
    requestAnimationFrame(() => {
      const pos = start + snippet.length;
      el.focus();
      el.setSelectionRange(pos, pos);
    });
  };

  return (
    <aside className={shell}>
      <div className="border-b border-app-border px-4 py-4">
        <h2 className="text-sm font-semibold text-app-text">Eigenschaften</h2>
        <p className="mt-0.5 truncate text-xs text-app-text-subtle">{label}</p>
      </div>

      <div className="flex-1 overflow-y-auto p-4 app-scrollbar">
        <Section>
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <p className={SETTINGS_FIELD_LABEL}>Element</p>
              <p className="mt-1 truncate text-sm font-semibold text-app-text">{label}</p>
            </div>
            {element.locked ? (
              <span className="inline-flex shrink-0 items-center gap-1 rounded-md bg-app-surface-muted px-2 py-1 text-[11px] font-medium text-app-text-muted">
                <Lock className="h-3 w-3" />
                Gesperrt
              </span>
            ) : null}
          </div>
          {mandatory ? (
            <div className="mt-2.5 flex gap-2 rounded-md border border-app-border bg-app-surface-muted px-2.5 py-2 text-[11px] leading-snug text-app-text-muted">
              <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-400" />
              <span>Pflichtfeld — verschiebbar, aber nicht entfernbar.</span>
            </div>
          ) : null}
          {element.key === 'recipient' ? (
            <div className="mt-2.5">
              <button
                type="button"
                className={`${SETTINGS_BTN_OUTLINE} w-full !h-8 text-xs`}
                disabled={!!element.locked}
                onClick={() => onSnapRecipientToDinWindow?.()}
                title="Position und Größe auf das DIN-5008-Anschriftenfenster (Form B) setzen"
              >
                <MapPinned className="h-3.5 w-3.5" />
                An DIN-Fenster ausrichten
              </button>
              <p className="mt-1.5 text-[11px] leading-snug text-slate-400">
                Setzt die Adresse auf die Fensterposition nach DIN 5008 Form B (Fensterbrief).
              </p>
            </div>
          ) : null}
        </Section>

        {isLabeled ? (
          <>
            <Divider />
            <Section title="Label-Darstellung">
              <ToggleGroup>
                <ToggleBtn
                  active={labelPlacement === 'stacked'}
                  onClick={() => onChange({ labelPlacement: 'stacked' })}
                  title="Label über dem Wert"
                >
                  Oben
                </ToggleBtn>
                <ToggleBtn
                  active={labelPlacement === 'inline'}
                  onClick={() => onChange({ labelPlacement: 'inline' })}
                  title="Label und Wert in einer Zeile"
                >
                  Inline
                </ToggleBtn>
              </ToggleGroup>
              <div className="mt-3">
                <p className={`${SETTINGS_FIELD_LABEL} mb-1.5`}>Label-Text</p>
                <input
                  className={`${SETTINGS_INPUT_CLASS} !h-8`}
                  value={element.labelText ?? defaultFieldLabel}
                  onChange={(e) => onChange({ labelText: e.target.value })}
                  placeholder={defaultFieldLabel}
                  aria-label="Label-Text"
                />
                <p className="mt-1.5 text-[11px] leading-snug text-slate-400">
                  {element.key === 'documentTitle'
                    ? 'Text löschen, um nur die Nummer zu zeigen. Die Nummer bleibt immer sichtbar.'
                    : element.key === 'invoiceNumber'
                      ? 'Leer lassen für typabhängiges Label (Angebotsnr., Rechnungsnr., …).'
                      : element.key === 'issueDate'
                        ? 'Leer lassen für typabhängiges Label (Angebotsdatum, Rechnungsdatum, …).'
                        : element.key === 'leistungszeitraum'
                          ? 'Leer lassen für typabhängiges Label (Leistungszeitraum / Lieferdatum).'
                          : 'Text löschen, um das Label auszublenden.'}
                </p>
              </div>
            </Section>
          </>
        ) : null}

        {isTextEl ? (
          <>
            <Divider />
            <Section title="Ausrichtung">
              <ToggleGroup>
                <ToggleBtn
                  active={(element.align ?? 'left') === 'left'}
                  onClick={() => onChange({ align: 'left' })}
                  title="Links"
                >
                  <AlignLeft className="h-3.5 w-3.5" strokeWidth={1.75} />
                </ToggleBtn>
                <ToggleBtn
                  active={element.align === 'center'}
                  onClick={() => onChange({ align: 'center' })}
                  title="Zentriert"
                >
                  <AlignCenter className="h-3.5 w-3.5" strokeWidth={1.75} />
                </ToggleBtn>
                <ToggleBtn
                  active={element.align === 'right'}
                  onClick={() => onChange({ align: 'right' })}
                  title="Rechts"
                >
                  <AlignRight className="h-3.5 w-3.5" strokeWidth={1.75} />
                </ToggleBtn>
              </ToggleGroup>
            </Section>

            <Section title={isLabeled ? 'Wert-Stil' : 'Textstil'} className="mt-4">
              <ToggleGroup>
                <ToggleBtn
                  active={valueBoldActive}
                  onClick={() => onChange({ bold: isLabeled ? !(element.bold !== false) : !element.bold })}
                  title="Fett"
                  className="font-bold"
                >
                  B
                </ToggleBtn>
                <ToggleBtn
                  active={!!element.italic}
                  onClick={() => onChange({ italic: !element.italic })}
                  title="Kursiv"
                  className="italic"
                >
                  I
                </ToggleBtn>
                <ToggleBtn
                  active={!!element.underline}
                  onClick={() => onChange({ underline: !element.underline })}
                  title="Unterstrichen"
                  className="underline"
                >
                  U
                </ToggleBtn>
              </ToggleGroup>
              <div className="mt-3">
                <Stepper
                  label="Schriftgröße"
                  value={fontSize}
                  min={5}
                  max={18}
                  step={0.5}
                  onChange={(n) => onChange({ fontSizePt: n })}
                />
              </div>
            </Section>

            {isLabeled ? (
              <Section title="Label-Stil" className="mt-4">
                <ToggleGroup>
                  <ToggleBtn
                    active={!!element.labelBold}
                    onClick={() => onChange({ labelBold: !element.labelBold })}
                    title="Label fett"
                    className="font-bold"
                  >
                    B
                  </ToggleBtn>
                </ToggleGroup>
                <div className="mt-3">
                  <Stepper
                    label="Label-Schriftgröße"
                    value={labelFontSize}
                    min={5}
                    max={18}
                    step={0.5}
                    onChange={(n) => onChange({ labelFontSizePt: n })}
                  />
                </div>
                <div className="mt-3">
                  <p className={`${SETTINGS_FIELD_LABEL} mb-1.5`}>Label-Farbe</p>
                  <div className="flex flex-wrap gap-1.5">
                    {TEXT_SWATCHES.map((c) => {
                      const current = (element.labelColor ?? (labelPlacement === 'stacked' ? '#64748b' : '#374151')).toLowerCase();
                      return (
                        <button
                          key={`label-${c}`}
                          type="button"
                          title={c}
                          className={`h-7 w-7 rounded-md border transition-shadow ${
                            current === c.toLowerCase()
                              ? 'border-pipedrive-blue ring-2 ring-pipedrive-blue/25'
                              : 'border-app-border hover:border-app-border-strong'
                          }`}
                          style={{ background: c }}
                          onClick={() => onChange({ labelColor: c })}
                        />
                      );
                    })}
                    <label
                      className="relative h-7 w-7 cursor-pointer overflow-hidden rounded-md border border-dashed border-app-border-strong"
                      title="Eigene Farbe"
                    >
                      <span className="pointer-events-none absolute inset-0 flex items-center justify-center text-[10px] font-medium text-slate-400">
                        +
                      </span>
                      <input
                        type="color"
                        value={element.labelColor ?? (labelPlacement === 'stacked' ? '#64748b' : '#374151')}
                        className="absolute inset-0 cursor-pointer opacity-0"
                        onChange={(e) => onChange({ labelColor: e.target.value })}
                      />
                    </label>
                  </div>
                </div>
              </Section>
            ) : null}
          </>
        ) : null}

        {element.key === 'freeText' ? (
          <>
            <Divider />
            <Section title="Inhalt">
              <textarea
                ref={freeTextRef}
                className={SETTINGS_TEXTAREA_CLASS}
                rows={3}
                value={element.text ?? ''}
                onChange={(e) => onChange({ text: e.target.value })}
                placeholder="z. B. Ausführungsort: {{leistungsort}} • {{leistungszeitraumLabel}}: {{leistungszeitraum}}"
              />
              <p className="mt-2 text-[11px] leading-snug text-slate-400">
                Platzhalter werden beim PDF mit Live-Daten ersetzt; Werte erscheinen fett.
              </p>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {LAYOUT_MERGE_TOKENS.map((token) => (
                  <button
                    key={token}
                    type="button"
                    className="inline-flex h-7 items-center rounded-md border border-app-border bg-app-surface px-2 text-[11px] font-medium text-app-text-muted hover:bg-app-hover"
                    onClick={() => insertFreeTextToken(token)}
                    title={`{{${token}}} einfügen`}
                  >
                    {tokenChipLabel(token)}
                  </button>
                ))}
              </div>
            </Section>
          </>
        ) : null}

        <Divider />

        <Section title="Position & Größe">
          <div className="grid grid-cols-2 gap-2">
            <PtInput
              label="X"
              value={element.xPt}
              min={0}
              max={Math.max(0, PDF_A4_WIDTH_PT - element.wPt)}
              step={1}
              disabled={element.locked}
              onCommit={(n) => onChange({ xPt: n })}
            />
            <PtInput
              label="Y"
              value={element.yPt}
              min={0}
              max={Math.max(0, bandHeightPt - element.hPt)}
              step={1}
              disabled={element.locked}
              onCommit={(n) => onChange({ yPt: n })}
            />
            <PtInput
              label="Breite"
              value={element.wPt}
              min={widthMin}
              max={Math.max(widthMin, PDF_A4_WIDTH_PT - element.xPt)}
              step={widthIsThickness ? 0.25 : 1}
              disabled={element.locked}
              onCommit={(n) => onChange({ wPt: n })}
            />
            <PtInput
              label="Höhe"
              value={element.hPt}
              min={heightMin}
              max={Math.max(heightMin, bandHeightPt - element.yPt)}
              step={heightIsThickness ? 0.25 : 1}
              disabled={element.locked}
              onCommit={(n) => onChange({ hPt: n })}
            />
          </div>
        </Section>

        {isUploadableAsset ? (
          <>
            <Divider />
            <Section title="Bild">
              <div className="overflow-hidden rounded-md border border-app-border bg-app-surface-muted">
                <div className="flex h-28 items-center justify-center bg-app-surface">
                  {assetPreviewUrl ? (
                    <img
                      src={assetPreviewUrl}
                      alt={label}
                      className="max-h-full max-w-full object-contain p-2"
                    />
                  ) : (
                    <span className="px-3 text-center text-xs text-slate-400">
                      Noch kein Bild hochgeladen
                    </span>
                  )}
                </div>
                <div className="flex flex-wrap gap-2 border-t border-app-border p-2.5">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/png,image/jpeg"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      e.target.value = '';
                      if (file) void onUploadAsset?.(file);
                    }}
                  />
                  <button
                    type="button"
                    className={`${SETTINGS_BTN_OUTLINE} !h-8 flex-1 !px-2 text-xs`}
                    disabled={assetUploading || element.locked}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    {assetUploading ? (
                      <LoadingSpinner size="sm" />
                    ) : (
                      <ImagePlus className="h-3.5 w-3.5" />
                    )}
                    {assetPreviewUrl ? 'Ersetzen' : 'Hochladen'}
                  </button>
                  {assetPreviewUrl ? (
                    <button
                      type="button"
                      className={`${SETTINGS_BTN_DANGER_OUTLINE} !h-8 !px-2 text-xs`}
                      disabled={assetUploading || element.locked}
                      onClick={() => onClearAsset?.()}
                    >
                      Entfernen
                    </button>
                  ) : null}
                </div>
              </div>
              <p className="mt-2 text-[11px] leading-snug text-slate-400">
                PNG oder JPG. Nach dem Hochladen die Vorlage speichern.
              </p>
              {element.key === 'headerBanner' ? (
                <button
                  type="button"
                  className={`${SETTINGS_BTN_OUTLINE} mt-2 w-full !h-8 text-xs ${
                    element.imageGrayscale ? '!border-pipedrive-blue !text-app-text' : ''
                  }`}
                  disabled={element.locked}
                  onClick={() => onChange({ imageGrayscale: !element.imageGrayscale })}
                  aria-pressed={!!element.imageGrayscale}
                >
                  {element.imageGrayscale ? 'Graustufen an' : 'Als Graustufen'}
                </button>
              ) : null}
            </Section>
          </>
        ) : null}

        {element.key === 'divider' ? (
          <>
            <Divider />
            <Section title="Linie">
              <div className="flex h-8 overflow-hidden rounded-md border border-app-border bg-app-surface-muted p-0.5">
                <ToggleBtn
                  active={resolveLineOrientation(element) === 'horizontal'}
                  onClick={() => {
                    if (resolveLineOrientation(element) === 'horizontal') return;
                    onChange({
                      lineOrientation: 'horizontal',
                      wPt: Math.max(element.hPt, 24),
                      hPt: Math.max(LAYOUT_LINE_MIN_THICKNESS_PT, element.wPt),
                    });
                  }}
                  title="Horizontal"
                >
                  Horizontal
                </ToggleBtn>
                <ToggleBtn
                  active={resolveLineOrientation(element) === 'vertical'}
                  onClick={() => {
                    if (resolveLineOrientation(element) === 'vertical') return;
                    onChange({
                      lineOrientation: 'vertical',
                      wPt: Math.max(LAYOUT_LINE_MIN_THICKNESS_PT, element.hPt),
                      hPt: Math.max(element.wPt, 24),
                    });
                  }}
                  title="Vertikal"
                >
                  Vertikal
                </ToggleBtn>
              </div>
              <Stepper
                label="Stärke"
                value={lineThicknessPt(element)}
                min={LAYOUT_LINE_MIN_THICKNESS_PT}
                max={24}
                step={0.25}
                onChange={(n) =>
                  resolveLineOrientation(element) === 'vertical'
                    ? onChange({ wPt: n })
                    : onChange({ hPt: n })
                }
              />
            </Section>
            <Divider />
            <Section title="Farbe">
              <div className="flex flex-wrap gap-1.5">
                {TEXT_SWATCHES.map((c) => (
                  <button
                    key={c}
                    type="button"
                    title={c}
                    className={`h-7 w-7 rounded-md border transition-shadow ${
                      (element.color || '#111827').toLowerCase() === c.toLowerCase()
                        ? 'border-pipedrive-blue ring-2 ring-pipedrive-blue/25'
                        : 'border-app-border hover:border-app-border-strong'
                    }`}
                    style={{ background: c }}
                    onClick={() => onChange({ color: c })}
                  />
                ))}
                <label
                  className="relative h-7 w-7 cursor-pointer overflow-hidden rounded-md border border-dashed border-app-border-strong"
                  title="Eigene Farbe"
                >
                  <span className="pointer-events-none absolute inset-0 flex items-center justify-center text-[10px] font-medium text-slate-400">
                    +
                  </span>
                  <input
                    type="color"
                    value={element.color || '#111827'}
                    className="absolute inset-0 cursor-pointer opacity-0"
                    onChange={(e) => onChange({ color: e.target.value })}
                  />
                </label>
              </div>
            </Section>
          </>
        ) : null}

        {(isTextEl || isImgEl) &&
        element.key !== 'logo' &&
        element.key !== 'signatureImage' &&
        element.key !== 'headerBanner' ? (
          <>
            <Divider />
            <Section
              title={
                element.key === 'colorBlock' ? 'Füllfarbe' : isLabeled ? 'Wertfarbe' : 'Textfarbe'
              }
            >
              <div className="flex flex-wrap gap-1.5">
                {swatches.map((c) => (
                  <button
                    key={c}
                    type="button"
                    title={c}
                    className={`h-7 w-7 rounded-md border transition-shadow ${
                      colorVal.toLowerCase() === c.toLowerCase()
                        ? 'border-pipedrive-blue ring-2 ring-pipedrive-blue/25'
                        : 'border-app-border hover:border-app-border-strong'
                    }`}
                    style={{ background: c }}
                    onClick={() => onChange(colorProp === 'bg' ? { bg: c } : { color: c })}
                  />
                ))}
                <label
                  className="relative h-7 w-7 cursor-pointer overflow-hidden rounded-md border border-dashed border-app-border-strong"
                  title="Eigene Farbe"
                >
                  <span className="pointer-events-none absolute inset-0 flex items-center justify-center text-[10px] font-medium text-slate-400">
                    +
                  </span>
                  <input
                    type="color"
                    value={colorVal}
                    className="absolute inset-0 cursor-pointer opacity-0"
                    onChange={(e) =>
                      onChange(colorProp === 'bg' ? { bg: e.target.value } : { color: e.target.value })
                    }
                  />
                </label>
              </div>
            </Section>
          </>
        ) : null}

        {isImgEl && element.key === 'colorBlock' ? (
          <>
            <Divider />
            <Section title="Ecken">
              <Stepper
                label="Radius"
                value={element.radiusPt ?? 0}
                min={0}
                max={24}
                onChange={(n) => onChange({ radiusPt: n })}
              />
              <button
                type="button"
                className={`${SETTINGS_BTN_OUTLINE} mt-2 w-full !h-8 text-xs ${
                  element.border ? '!border-pipedrive-blue !bg-blue-50 !text-pipedrive-blue' : ''
                }`}
                onClick={() => onChange({ border: !element.border })}
              >
                {element.border ? 'Rahmen aktiv' : 'Rahmen hinzufügen'}
              </button>
            </Section>
          </>
        ) : null}

        <Divider />

        <div className="space-y-2 pb-2">
          <button
            type="button"
            className={`${SETTINGS_BTN_OUTLINE} w-full !h-8 text-xs`}
            onClick={onLock}
          >
            {element.locked ? <Unlock className="h-3.5 w-3.5" /> : <Lock className="h-3.5 w-3.5" />}
            {element.locked ? 'Entsperren' : 'Element sperren'}
          </button>
          {canRemove ? (
            <button
              type="button"
              className={`${SETTINGS_BTN_DANGER_OUTLINE} w-full !h-8 text-xs`}
              onClick={onRemove}
            >
              <Trash2 className="h-3.5 w-3.5" />
              Entfernen
            </button>
          ) : null}
        </div>
      </div>
    </aside>
  );
}
